import * as THREE from "three";

/**
 * Drops the CPU copy of geometry and textures once the main renderer has them on the GPU (M31 memory pass,
 * docs/PERF.md "M31").
 *
 * three.js keeps every vertex array and every decoded texture (a KTX2 texture's transcoded mip chain) after
 * uploading it, so a long session held much of it twice: ~20 MB more ArrayBuffers after every level on the hub,
 * 152 → ~290 MB over six levels, on a phone that kills the page under memory pressure. What is
 * registered here is emptied right after its first upload — `BufferAttribute.onUploadCallback` for an attribute,
 * `Texture.onUpdate` for a texture — and only after an upload by the MAIN canvas's renderer (`SceneRig.render` sets
 * `mainDrawing`): the chest stage, the skins sheet's turntable and the debug renderers each own a WebGL context and
 * upload what they draw again from these arrays.
 *
 * The rules (the callers decide what is registered; test/cpuRelease.test.ts pins them):
 * - only what nothing reads on the CPU again: draw-only derived geometry (the towers' reduced twins, towerLod.ts,
 *   and the hub's reduced stands, hubLod.ts),
 *   textures of library models no other context draws from the library (`AssetLibrary.SharedWithOtherContexts`;
 *   the skins turntable builds from private copies, `AssetLibrary.copies`), the terrain's KTX2
 *   twins. Library geometry stays: level builds merge it (staticBatch.ts), reduce it (lod.ts) and bake rigs from it;
 * - a geometry's bounds are computed before its arrays go (frustum culling and Box3.setFromObject read those);
 * - an attribute that is written again (dynamic usage, update ranges) or shares an interleaved buffer is kept;
 * - a texture is released through its own reference only (`mipmaps` replaced, never emptied in place), so a clone
 *   made earlier (the additive glow's level-0 copy, hqMaterials.ts) still uploads from the mip objects it holds;
 * - nothing re-uploads on its own after this: a lost WebGL context that comes back would have to, so once anything
 *   has been released the rig reloads the page on restore instead (scene.ts `onContextRestored`).
 *
 * `?keepcpu` turns it off (A/B measurements, and a way out if a CPU reader was missed), and so do the capture pages
 * `?allhq` and `?sheet`: their debug renderers (IconRender, SkinThumbs, the readability sheet) draw library models in
 * contexts of their own.
 */
export class CpuRelease {
  static enabled = typeof location === "undefined" || !["keepcpu", "allhq", "sheet"].some((k) => new URLSearchParams(location.search).has(k));
  /** True while the main canvas's renderer draws (SceneRig.render); uploads by any other renderer keep their arrays. */
  static mainDrawing = false;
  /** Arrays and textures released so far, and their bytes (Settings → Copy diagnostics, the headless memory runs). */
  static readonly stats = { attributes: 0, textures: 0, bytes: 0 };
  private static readonly marked = new WeakSet<object>();
  private static readonly released = new WeakSet<THREE.Texture>();
  /** Uploads of something already released (it draws empty or black): counted, and logged once (diagnostics). */
  static uploadsAfterRelease = 0;

  /**
   * Something released was uploaded again — a rule above was broken (it was disposed and drawn again, changed, or
   * drawn by another context). It is empty on the GPU now; the warning lands in Settings → Copy diagnostics.
   */
  static uploadedAgain(what: string): void {
    if (CpuRelease.uploadsAfterRelease++ === 0) console.warn(`cpuRelease: ${what} was uploaded again after its CPU copy was released (reload with ?keepcpu to compare)`);
  }

  /** Whether anything has been released: a restored context cannot rebuild it, so the page reloads instead. */
  static get anyReleased(): boolean { return CpuRelease.stats.attributes + CpuRelease.stats.textures > 0; }

  /** Runs `draw` as the main renderer's frame: what it uploads is released. */
  static asMain<T>(draw: () => T): T {
    const was = CpuRelease.mainDrawing;
    CpuRelease.mainDrawing = true;
    try { return draw(); } finally { CpuRelease.mainDrawing = was; }
  }

  /**
   * A geometry that is only ever drawn from now on: bounds computed now, then every static attribute and the index
   * emptied once the main renderer has uploaded it.
   */
  static geometry(geo: THREE.BufferGeometry): void {
    if (!CpuRelease.enabled || CpuRelease.marked.has(geo)) return;
    CpuRelease.marked.add(geo);
    if (!geo.boundingBox) geo.computeBoundingBox();
    if (!geo.boundingSphere) geo.computeBoundingSphere();
    for (const a of Object.values(geo.attributes)) CpuRelease.attribute(a);
    if (geo.index) CpuRelease.attribute(geo.index);
  }

  /**
   * A skinned mesh measures its bounds (frustum culling, Box3.setFromObject) from its CURRENT geometry's vertices
   * where the bones hold them, once per instance: drawn at a reduced copy whose arrays are released, that would read
   * nothing. `full` — the geometry the copy was reduced from — has the same parts on the same bones (a superset of
   * the copy's vertices), so it measures the same box.
   */
  static boundsFromFull(m: THREE.SkinnedMesh, full: THREE.BufferGeometry): void {
    const measure = (fn: () => void): void => {
      const shown = m.geometry;
      m.geometry = full;
      try { fn(); } finally { m.geometry = shown; }
    };
    m.computeBoundingBox = function (this: THREE.SkinnedMesh): void { measure(() => THREE.SkinnedMesh.prototype.computeBoundingBox.call(this)); };
    m.computeBoundingSphere = function (this: THREE.SkinnedMesh): void { measure(() => THREE.SkinnedMesh.prototype.computeBoundingSphere.call(this)); };
  }

  /** Whether an attribute's array may go after upload: static, not interleaved, not instanced, never partially updated. */
  static releasable(a: THREE.BufferAttribute | THREE.InterleavedBufferAttribute): a is THREE.BufferAttribute {
    if ((a as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute) return false;
    const b = a as THREE.BufferAttribute;
    if ((b as THREE.InstancedBufferAttribute).isInstancedBufferAttribute) return false;
    return b.usage === THREE.StaticDrawUsage && b.updateRanges.length === 0 && b.array.length > 0;
  }

  private static attribute(a: THREE.BufferAttribute | THREE.InterleavedBufferAttribute): void {
    if (!CpuRelease.releasable(a) || CpuRelease.marked.has(a)) return;
    CpuRelease.marked.add(a);
    const prev = a.onUploadCallback;
    a.onUploadCallback = function (this: THREE.BufferAttribute): void {
      prev.call(this);
      if (this.array.length === 0) { CpuRelease.uploadedAgain(`attribute ${this.name || ""}`.trim()); return; }
      if (!CpuRelease.mainDrawing) return;
      CpuRelease.stats.attributes++;
      CpuRelease.stats.bytes += this.array.byteLength;
      // the same type, empty: `count`, `itemSize` and the GPU buffer's type stay as uploaded
      this.array = new (this.array.constructor as new (n: number) => THREE.TypedArray)(0);
    };
  }

  /** A texture only the main renderer draws: its compressed mip chain goes once the main renderer has it. */
  static texture(t: THREE.Texture): void {
    if (!CpuRelease.enabled || CpuRelease.marked.has(t)) return;
    CpuRelease.marked.add(t);
    const prev = t.onUpdate;
    // three calls it as `texture.onUpdate(texture)` after each upload of this texture
    t.onUpdate = function (this: THREE.Texture): void {
      prev?.call(this);
      if (CpuRelease.released.has(this)) CpuRelease.uploadedAgain(`texture ${this.name}`.trim());
      else if (CpuRelease.mainDrawing) CpuRelease.drop(this);
    };
  }

  /** Every texture on the materials under `root` (a model the other contexts never draw). */
  static textures(root: THREE.Object3D): void {
    root.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      for (const m of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
        for (const v of Object.values(m)) if ((v as THREE.Texture | null)?.isTexture) CpuRelease.texture(v as THREE.Texture);
      }
    });
  }

  /**
   * A compressed texture's mip chain. Not an ImageBitmap (the WebP set on a GPU without a compressed format): those
   * live on the texture's Source, which GLTFLoader shares between the textures of one image, so emptying it for one
   * would take it from another that has not uploaded yet.
   */
  private static drop(t: THREE.Texture): void {
    if (!(t as THREE.CompressedTexture).isCompressedTexture || !t.mipmaps || t.mipmaps.length === 0) return;
    let bytes = 0;
    for (const m of t.mipmaps as { data?: ArrayBufferView }[]) bytes += m?.data?.byteLength ?? 0;
    t.mipmaps = [];                                             // this texture's list only: a clone keeps its own
    CpuRelease.released.add(t);
    CpuRelease.stats.textures++;
    CpuRelease.stats.bytes += bytes;
  }
}
